import { useEffect, useLayoutEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../cn';
import { POPOVER_ATTRIBUTE, invoker, rememberPopoverOrigin } from './modalLayers';
import styles from './Popover.module.css';

export interface PopoverProps {
  open: boolean;
  onClose: () => void;
  /** Anchor rectangle; typically from a button's getBoundingClientRect(). */
  anchor: DOMRect | null;
  placement?: 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';
  children: ReactNode;
  className?: string;
  width?: number;
  /**
   * Hint for vertical-flip math when the requested side would push off
   * the bottom (or top) of the viewport. We can't measure the actual
   * panel height before render, so callers with unusually tall menus
   * can pass an override; otherwise we assume ~240px (covers most
   * single-pane Menu lists).
   */
  estimatedHeight?: number;
  /**
   * When true, render a faint scrim behind the popover so it stands
   * apart from a busy parent (e.g., when opened from within a Modal).
   * The scrim catches outside-clicks like the document listener does;
   * keeping both means the dismiss path works even if the popover
   * isn't full-screen.
   */
  withBackdrop?: boolean;
}

/**
 * Floating positioned panel, dismisses on outside click or Escape.
 */
export function Popover({ open, onClose, anchor, placement = 'bottom-start', children, className, width, estimatedHeight, withBackdrop }: PopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  /**
   * What opened this, its trigger (`invoker`), kept for a dialog opened from
   * inside it: a menu item that opens a dialog is gone when the dialog closes,
   * and focus goes back here instead (TOADO-984, review round 1). Read while
   * rendering the open, before anything inside takes focus.
   */
  const origin = useRef<HTMLElement | null>(null);
  const openCommitted = useRef(false);
  if (open && !openCommitted.current) origin.current = invoker();
  useLayoutEffect(() => {
    openCommitted.current = open;
    const popover = ref.current;
    if (!open || !popover) return;
    rememberPopoverOrigin(popover, origin.current);
    return () => rememberPopoverOrigin(popover, null);
  }, [open, anchor]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // Claimed, so a listener after this one (an onboarding tour) knows the key was used.
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', onKey);
    // defer so the opening click doesn't immediately close
    const t = setTimeout(() => document.addEventListener('click', onClick), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onClick);
      clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open || !anchor) return null;

  // Viewport-aware placement. If the requested side would push the
  // popover off-screen, flip to the other side instead of clamping —
  // clamping detaches the popover from its anchor and looks broken.
  // Width hint defaults to 280px (covers menus + pickers); height hint
  // defaults to 240px (covers a single-pane Menu).
  const VIEWPORT_PAD = 8;
  const vw = typeof window !== 'undefined' ? window.innerWidth : Infinity;
  const vh = typeof window !== 'undefined' ? window.innerHeight : Infinity;
  const estimatedWidth = width ?? 280;
  const estHeight = estimatedHeight ?? 240;

  // ---- Horizontal flip ----
  const wantsEnd = placement.endsWith('end');
  const projectedLeft = wantsEnd ? anchor.right - estimatedWidth : anchor.left;
  const projectedRight = projectedLeft + estimatedWidth;

  let useEnd = wantsEnd;
  if (wantsEnd && projectedLeft < VIEWPORT_PAD) {
    useEnd = false;
  } else if (!wantsEnd && projectedRight > vw - VIEWPORT_PAD) {
    useEnd = true;
  }

  // ---- Vertical flip ----
  // Default to opening below the anchor; flip up if the bottom edge
  // would clip the viewport AND there's more room above. Cards near
  // the bottom of a board column hit this constantly.
  const wantsBottom = placement.startsWith('bottom');
  const spaceBelow = vh - anchor.bottom;
  const spaceAbove = anchor.top;
  let useBottom = wantsBottom;
  if (wantsBottom && spaceBelow < estHeight + VIEWPORT_PAD && spaceAbove > spaceBelow) {
    useBottom = false;
  } else if (!wantsBottom && spaceAbove < estHeight + VIEWPORT_PAD && spaceBelow > spaceAbove) {
    useBottom = true;
  }

  const style: React.CSSProperties = { width };
  const top = useBottom ? anchor.bottom + 6 : anchor.top - 6;
  const transform = useBottom ? undefined : 'translateY(-100%)';
  const left = useEnd ? anchor.right : anchor.left;
  const align = useEnd ? 'translateX(-100%)' : undefined;
  style.top = top;
  style.left = left;
  style.transform = [transform, align].filter(Boolean).join(' ');

  // Render via a portal so the popover escapes any ancestor that's a
  // containing block for `position: fixed` (e.g., a Modal whose CSS
  // animation leaves a non-`none` transform on the dialog — that flips
  // fixed-positioning to be relative to the dialog, not the viewport,
  // and the popover lands in the wrong place + clips against the
  // dialog's overflow:hidden).
  if (typeof document === 'undefined') return null;
  return createPortal(
    <>
      {withBackdrop && <div className={styles.backdrop} onClick={onClose} aria-hidden />}
      <div ref={ref} {...{ [POPOVER_ATTRIBUTE]: '' }} className={cn(styles.popover, className)} style={style} role="dialog">
        {children}
      </div>
    </>,
    document.body,
  );
}
